import React, { Component } from "react";

import { WebSportItem } from "../../../constants";
import styles from "./styles.scss";
import MatchupTeamSelector from "../MatchupTeamSelector";
import TeamLogo from "../../Team/TeamLogo";
import AnalyticsService from "../../../services/AnalyticsService";

interface MatchupTeamsProps {
  event: any;
  currentSportObj: WebSportItem;
  panelName: string;
  trackingKey: string;
  awayTeamContent: any;
  homeTeamContent: any;
}

interface MatchupTeamsState {
  homeTeamSelected: boolean;
}

/**
 * The MatchupTeams object is used to render side-by-side list data for a matchup between two teams.
 * Examples of this are injury lists and lineup lists.
 *
 * This component takes a name for the panel which is displayable to the user, an event to pull requisite objects
 * from and the awayTeamContent and homeTeamContent that is displayed within these containers.
 *
 * There is also a maxHeight prop that can be passed in optionally which is a number of pixels that the container should
 * be limited to.  If this is passed in and the content is greater than this maximum height the user will be able to
 * choose to show all and expand the component to whatever size is needed to show all data.  They have the option to
 * recollapse the component.  Note that this is a mobile ready component and maximum height behavior does not activate
 * on small screens.
 */
class MatchupTeams extends Component<MatchupTeamsProps, MatchupTeamsState> {
  constructor(props: MatchupTeamsProps) {
    super(props);
    this.state = {
      homeTeamSelected: false,
    };
  }

  /**
   * Wrap the team content in an overflow detection element and display a see more button if needed.
   * This also renders the team logo at the head of the panel for any screen medium and up.
   *
   * @param isHome {boolean} are we rendering the home team content or the away team content
   */
  _createWrappedTeamContent = (isHome: boolean) => {
    const { panelName, awayTeamContent, homeTeamContent, event, currentSportObj } = this.props;

    const content = isHome ? homeTeamContent : awayTeamContent;
    const team = isHome ? event.homeTeam : event.awayTeam;
    const leageName = (event.league && event.league.name) || "";

    return (
      <div className={`rotoql-team__panel ${isHome ? "rotoql-team__panel--left" : "rotoql-team__panel--right"}`}>
        <div className="rotoql-team-header__container">
          <TeamLogo
            className="rotoql-team-header__logo"
            leagueName={leageName}
            teamAbbreviation={currentSportObj.isTennis ? team.country : team.preferredAbbreviation}
            size={32}
          />
          &nbsp;
          <div className="rotoql-team-header__team-title">
            {team.lastName} {panelName}
          </div>
        </div>
        {content}
      </div>
    );
  };

  render() {
    const { event, panelName, trackingKey } = this.props;
    const { homeTeamSelected } = this.state;
    const { awayTeam, homeTeam, league, startDate } = event;

    return (
      <div className="rotoql-matchup-teams-container">
        {/* Medium screens and up we show both teams side by side or on top of each other depending on size, this is determined by CSS rules.*/}
        <div className="rotoql-matchup-teams-container__teams-con">
          {this._createWrappedTeamContent(false)}
          {this._createWrappedTeamContent(true)}
        </div>
        {/* For small screens we render a matchup selector box at top and only one team panel depending on the user selection of which team they want to see */}
        <div className="rotoql-matchup-teams-container__mobile-teams-con">
          <MatchupTeamSelector
            homeTeam={homeTeam}
            descriptor={panelName}
            awayTeam={awayTeam}
            homeTeamSelected={homeTeamSelected}
            onTeamSelection={(selected: boolean) => {
              this.setState({ homeTeamSelected: selected });
              let awayTeamName: string, homeTeamName: string;
              if (league.name === "CFB" || league.name === "CBK") {
                awayTeamName = `${awayTeam.fullName} ${awayTeam.lastName}`;
                homeTeamName = `${homeTeam.fullName} ${homeTeam.lastName}`;
              } else {
                awayTeamName = awayTeam.fullName;
                homeTeamName = homeTeam.fullName;
              }
              AnalyticsService.track("Change " + trackingKey, {
                screen: "GameView",
                sport: league.name,
                awayTeam: awayTeamName,
                homeTeam: homeTeamName,
                startDate,
                selectedTeam: selected ? homeTeam.preferredAbbreviation : awayTeam.preferredAbbreviation,
              });
            }}
          />
          {this._createWrappedTeamContent(homeTeamSelected)}
        </div>
        <style jsx global>
          {styles}
        </style>
      </div>
    );
  }
}

export default MatchupTeams;
